import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [threats, setThreats] = useState([]);
  const [devices, setDevices] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch threats
      const threatsResponse = await fetch(`${API_URL}/threats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const threatsData = threatsResponse.ok ? await threatsResponse.json() : [];
      
      // Fetch devices
      const devicesResponse = await fetch(`${API_URL}/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const devicesData = devicesResponse.ok ? await devicesResponse.json() : [];
      
      // Fetch violations
      const violationsResponse = await fetch(`${API_URL}/violations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const violationsData = violationsResponse.ok ? await violationsResponse.json() : [];
      
      setThreats(Array.isArray(threatsData) ? threatsData : []);
      setDevices(Array.isArray(devicesData) ? devicesData : []);
      setViolations(Array.isArray(violationsData) ? violationsData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setThreats([]);
      setDevices([]);
      setViolations([]);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    activeThreats: threats.filter(t => t.status === 'active' || t.status === 'Active').length,
    totalDevices: devices.length,
    onlineDevices: devices.filter(d => d.status === 'online' || d.status === 'Online').length,
    violations: violations.length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Security Dashboard</h1>
          <p className="text-gray-400 mt-1">Real-time security monitoring and analytics</p>
        </div>

        {/* Clickable Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Active Threats - Click to go to Threat Detection */}
          <button
            onClick={() => navigate('/threat-detection')}
            className="bg-cyber-card border border-gray-800 rounded-lg p-6 hover:border-red-500 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">Active Threats</h3>
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats.activeThreats}</div>
            <p className="text-sm text-gray-400">Click to view details →</p>
          </button>

          {/* Devices Online - Click to go to Devices page */}
          <button
            onClick={() => navigate('/devices')}
            className="bg-cyber-card border border-gray-800 rounded-lg p-6 hover:border-green-500 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">Devices Online</h3>
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {stats.onlineDevices}/{stats.totalDevices}
            </div>
            <p className="text-sm text-gray-400">Click to view details →</p>
          </button>

          {/* Policy Violations - Click to go to Access Control */}
          <button
            onClick={() => navigate('/access-control')}
            className="bg-cyber-card border border-gray-800 rounded-lg p-6 hover:border-yellow-500 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">Policy Violations</h3>
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats.violations}</div>
            <p className="text-sm text-gray-400">Click to view details →</p>
          </button>

          {/* Security Score - Not clickable */}
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">Security Score</h3>
              <div className="w-10 h-10 bg-cyber-cyan/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-cyber-cyan" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">87/100</div>
            <p className="text-sm text-gray-400">Good standing</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Threats */}
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Recent Threats</h3>
              <button
                onClick={() => navigate('/threat-detection')}
                className="text-cyber-cyan hover:text-cyan-400 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            <div className="space-y-4">
              {threats.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No threats detected</p>
              ) : (
                threats.slice(0, 5).map((threat) => (
                  <div key={threat.id} className="bg-cyber-darker border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-white font-medium">{threat.threat_type || threat.type}</h4>
                        <p className="text-sm text-gray-400">{threat.device_name || 'Unknown Device'}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${
                        threat.severity === 'Critical'
                          ? 'bg-red-500/20 text-red-400'
                          : threat.severity === 'High'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {threat.severity}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Device Status */}
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Connected Devices</h3>
              <button
                onClick={() => navigate('/devices')}
                className="text-cyber-cyan hover:text-cyan-400 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            <div className="space-y-4">
              {devices.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No devices registered</p>
              ) : (
                devices.slice(0, 5).map((device) => (
                  <div key={device.id} className="bg-cyber-darker border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{device.device_name}</h4>
                        <p className="text-sm text-gray-400">{device.os || 'Unknown OS'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Last scan: {device.last_scan ? new Date(device.last_scan).toLocaleString() : 'Never'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded whitespace-nowrap ${
                        device.status === 'online' || device.status === 'Online'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {device.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;